<script setup lang="ts">
/* eslint-env vue/setup-compiler-macros */
import { watchEffect, watch, onUnmounted, ref, nextTick } from 'vue';
import { storeToRefs } from 'pinia';
import Drawer from 'primevue/drawer';
import NotificationEntry from '@/components/notifications/NotificationEntry.vue';
import { useNotificationsStore } from '@/stores/notifications';

const notificationsStore = useNotificationsStore();
const {
    notifications,
    isDrawerOpen,
    isLoading,
    isLoadingMore,
    hasMore,
} = storeToRefs(notificationsStore);
const {
    closeDrawer,
    markAllAsRead,
    loadMore,
} = notificationsStore;

const scrollContainer = ref<HTMLElement | null>(null);
const sentinel = ref<HTMLElement | null>(null);
let observer: IntersectionObserver | null = null;

watchEffect(() => {
    document.body.style.overflow = isDrawerOpen.value ? 'hidden' : '';
});

watch(isDrawerOpen, async (open) => {
    if (open && !observer) {
        await nextTick();
        if (sentinel.value && scrollContainer.value) {
            observer = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting && !isLoading.value && !isLoadingMore.value && hasMore.value) {
                        loadMore();
                    }
                },
                { root: scrollContainer.value, threshold: 0 },
            );
            observer.observe(sentinel.value);
        }
    }
});

onUnmounted(() => {
    document.body.style.overflow = '';
    observer?.disconnect();
    observer = null;
});
</script>

<template>
    <Drawer
        v-model:visible="isDrawerOpen"
        position="right"
        :showCloseIcon="false"
        :pt="{
            root: { class: 'notification-drawer' },
            mask: { class: 'notification-drawer-mask' },
            content: { class: 'notification-drawer__content' },
            header: { class: 'notification-drawer__header' },
        }"
    >
        <template #header>
            <div class="notification-drawer__header-inner">
                <div class="notification-drawer__header-left">
                    <h2 class="notification-drawer__title">Notifications</h2>

                    <button
                        class="notification-drawer__mark-read"
                        type="button"
                        @click="markAllAsRead"
                    >
                        Mark All as Read
                    </button>
                </div>

                <button
                    class="notification-drawer__close"
                    type="button"
                    aria-label="Close notifications"
                    @click="closeDrawer"
                >
                    <i class="pi pi-times" aria-hidden="true" />
                </button>
            </div>
        </template>

        <div ref="scrollContainer" class="notification-drawer__scroll">
            <div class="notification-drawer__list">
                <NotificationEntry
                    v-for="item in notifications"
                    :key="item.id"
                    :message="item.message"
                    :bold-segments="item.boldSegments"
                    :created-at="item.createdAt"
                    :is-read="item.isRead"
                    :href="item.href"
                    @click="notificationsStore.handleNotificationClick(item.id)"
                />
            </div>

            <div v-if="isLoadingMore" class="notification-drawer__loading-more">
                <i class="pi pi-spin pi-spinner" aria-hidden="true" />
            </div>

            <div ref="sentinel" class="notification-drawer__sentinel" />
        </div>
    </Drawer>
</template>

<style lang="scss">
.notification-drawer {
    width: 358px !important;
    background: $white-color !important;
    box-shadow: -4px 0 24px rgba($black-color, 0.08) !important;
    border: none !important;

    &__header {
        padding: 28px 20px 20px !important;
        border-bottom: none !important;
    }

    &__header-inner {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        width: 100%;
    }

    &__header-left {
        display: flex;
        align-items: baseline;
        gap: 14px;
    }

    &__title {
        margin: 0;
        font-family: $font-family-archivo;
        font-weight: 700;
        font-size: 22px;
        line-height: 1.2;
        color: $black-color;
    }

    &__mark-read {
        padding: 0;
        border: none;
        background: none;
        font-family: $font-family-roboto;
        font-weight: 300;
        font-size: 13px;
        line-height: 1;
        color: rgba($black-color, 0.6);
        text-decoration: underline solid;
        text-underline-offset: 25%;
        text-decoration-thickness: 0%;
        text-decoration-skip-ink: auto;
        cursor: pointer;

        &:hover {
            color: $black-color;
        }
    }

    &__close {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        border: none;
        background: none;
        cursor: pointer;
        color: $main-orange;

        .pi {
            font-size: 16px;
            font-weight: 900;
        }

        &:hover {
            opacity: 0.8;
        }
    }

    &__content {
        padding: 0 !important;
        overflow: hidden !important;
    }

    &__scroll {
        height: 100%;
        padding: 0 20px 20px;
        overflow-y: auto;
        overscroll-behavior: contain;
        scrollbar-width: none;

        &::-webkit-scrollbar {
            display: none;
        }
    }

    &__list {
        display: flex;
        flex-direction: column;
        gap: 15px;
    }

    &__loading-more {
        display: flex;
        justify-content: center;
        padding: 12px 0 4px;
        color: $main-orange;

        .pi {
            font-size: 18px;
        }
    }

    &__sentinel {
        height: 1px;
    }
}

.notification-drawer-mask {
    background: $color-divider-subtle !important;
}
</style>
